import { useEffect, useState } from 'react';
import { enablePush, disablePush, currentPushSubscription, pushSupported } from '../lib/push';
import { snapshotSync, refreshSnapshot } from '../db/snapshot';
import { postAction } from '../api/client';
import { currentSession } from '../auth/session';

/** "Notify me when a reading is overdue" — a per-device opt-in, usable by any signed-in user. */
export default function PushToggle() {
  const supported = pushSupported();
  const [subscribed, setSubscribed] = useState<boolean | null>(null); // null = still checking
  const [vapidKey, setVapidKey] = useState<string | undefined>(() => snapshotSync()?.config.vapidPublicKey);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [testMsg, setTestMsg] = useState('');

  useEffect(() => {
    if (!supported) { setSubscribed(false); return; }
    currentPushSubscription().then((s) => setSubscribed(!!s)).catch(() => setSubscribed(false));
  }, [supported]);

  useEffect(() => {
    if (vapidKey) return;
    refreshSnapshot().then((s) => setVapidKey(s?.config.vapidPublicKey)).catch(() => {});
  }, [vapidKey]);

  if (!supported) return null; // e.g. iOS Safari not installed to the home screen

  async function sendTest() {
    const ses = currentSession();
    if (!ses) return;
    setBusy(true); setErr(''); setTestMsg('');
    try {
      const res = await postAction<{ ok: boolean; message?: string }>('sendTestPush', { token: ses.token });
      setTestMsg(res.ok ? 'Sent — it should arrive any moment.' : (res.message || 'Could not send.'));
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not send the test notification.');
    } finally {
      setBusy(false);
    }
  }

  async function toggle() {
    if (!vapidKey) { setErr('Push notifications are not set up yet — ask an admin.'); return; }
    setBusy(true); setErr('');
    try {
      if (subscribed) { await disablePush(); setSubscribed(false); }
      else { await enablePush(vapidKey); setSubscribed(true); }
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not update notification settings.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card">
      <div className="spread">
        <div>
          <strong>Overdue-reading alerts</strong>
          <p className="muted tiny" style={{ margin: '2px 0 0' }}>
            Push a notification to this device when a reading is overdue.
          </p>
        </div>
        <button className={subscribed ? 'secondary' : ''} style={{ width: 'auto', margin: 0, minHeight: 40 }}
          onClick={() => void toggle()} disabled={busy || subscribed === null}>
          {busy ? '…' : subscribed ? 'On' : 'Off'}
        </button>
      </div>
      {subscribed && (
        <button className="secondary sm" style={{ marginTop: 10 }} onClick={() => void sendTest()} disabled={busy}>
          Send a test notification
        </button>
      )}
      {testMsg && <p className="muted tiny" style={{ marginTop: 8 }}>{testMsg}</p>}
      {err && <p className="error tiny" style={{ marginTop: 8 }}>{err}</p>}
    </div>
  );
}
